import type { PartialExcept } from "@pdfking/structs"
import type { StringGUID } from "@pdfking/tfunc"
import { RecordAssign } from "@pdfking/tfunc"

import type { FileInfo } from "#public"
import type { StandardResponse } from "#public"

import { ContentETag } from "./ContentETag"
import { ResponseContentLength } from "./ResponseContentLength"
import { ResponseContentType } from "./ResponseContentType"
import { ResponseFileName } from "./ResponseFileName"
import { ResponseHeader } from "./ResponseHeader"
import { ResponseModifiedTime } from "./ResponseModifiedTime"

/** A Response somebody else built, read as what it carries.
 *
 * The other half of ResponseCreate: a download off the network, an entry out of
 * a Cache, anything that arrived with its headers already written. Everything
 * comes off those headers, because whoever wrote them knew what they held —
 * and the etag is either declared or derived from the size and the write time,
 * so two readers of one entry agree without a second rule.
 *
 * `name` is what the caller asked for — a path in the storage that answered, a
 * url that was fetched — and is what this is called unless the response said
 * so itself: a writer that names its entries sends `content-disposition`, and
 * ResponseFileName reads back what ResponseCreate wrote. Either way there is
 * one, which is what the etag is derived over when none was declared. Nothing
 * adopts a response it did not ask for, so every caller has one to give.
 *
 * The rest of `info` overrides the headers for the caller who knows better: a
 * content store's key *is* its etag, and a storage that keeps bytes without
 * headers has nowhere to have kept the one that was written. */
export const ResponseAdopt = (
  response: Response,
  { name, ...info }: PartialExcept<FileInfo, "name">
): StandardResponse => {
  const named = ResponseFileName(response, name)

  return RecordAssign(response, {
    info: {
      // Declared wins: a store that knows what it holds wrote the etag with
      // the bytes, and that header survives the trip. Nothing said it, so it
      // comes off the name and the two things the response does declare.
      etag:
        (ResponseHeader(response, "etag") as StringGUID) ||
        ContentETag(ResponseContentLength(response), ResponseModifiedTime(response), named),
      modified: ResponseModifiedTime(response),
      name: named,
      size: ResponseContentLength(response),
      type: ResponseContentType(response),
      ...info,
    },
  })
}
